import React, { useState, useEffect } from 'react';
import { Grid, Card, CardContent, Typography, Box, CircularProgress, IconButton, Tooltip, Paper, TextField, Button, Alert } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import { getStats } from '../api';

const StatCard = ({ title, value, color, icon, onClick, onReset, onRestore }) => (
  <Card onClick={onClick} sx={{
    bgcolor: '#1a1a2e', border: '1px solid #333', borderRadius: 2,
    cursor: onClick ? 'pointer' : 'default', transition: 'all 0.2s', position: 'relative',
    '&:hover': onClick ? { border: '1px solid var(--ac1, #7c4dff)', transform: 'translateY(-2px)' } : {}
  }}>
    <CardContent>
      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <Typography variant="body2" sx={{ color: '#aaa' }}>{icon} {title}</Typography>
        <Box sx={{ display:'flex', gap:0 }}>
          {onReset && <Tooltip title="Скинути до 0"><IconButton size="small" onClick={e=>{e.stopPropagation();onReset();}} sx={{ color:'#555','&:hover':{color:'#f44'}, p:0.3 }}><RefreshIcon sx={{ fontSize:14 }}/></IconButton></Tooltip>}
          {onRestore && <Tooltip title="Відновити"><IconButton size="small" onClick={e=>{e.stopPropagation();onRestore();}} sx={{ color:'#555','&:hover':{color:'#4caf50'}, p:0.3 }}>↩️</IconButton></Tooltip>}
        </Box>
      </Box>
      <Typography variant="h4" sx={{ color: color || 'var(--ac1, #7c4dff)', fontWeight: 'bold', mt: 1 }}>{value}</Typography>
    </CardContent>
  </Card>
);

const DEFAULT_ORDER = [0,1,2,3,4];
const STORAGE_KEY = 'stats_card_order';

export default function StatsPage() {
  const [stats, setStats] = useState(null);
  // 💎 PlayLanguage (Phase F fix, OWNER): show PL revenue in Statistics too, not only
  // on the Finance page. Reuses the additive /api/finance/products breakdown.
  const [products, setProducts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || DEFAULT_ORDER; } catch { return DEFAULT_ORDER; }
  });
  const navigate = useNavigate();
  const tok = localStorage.getItem('dashboard_token');
  // [МЕДІА УНІКАЛІЗАТОР] D-221 C: founder/promo config (editable N/9день/€13/L1%)
  const [founders, setFounders] = useState(null);
  const [fN, setFN] = useState(''); const [fDaily, setFDaily] = useState(''); const [fL1, setFL1] = useState('');
  const [fP, setFP] = useState({}); const [fMsg, setFMsg] = useState(''); const [fErr, setFErr] = useState('');
  const fInit = React.useRef(false);
  const tfSx = { '& input': { color: '#fff' }, '& label': { color: '#aaa' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333' } };
  // [D-221 C.1] Margin Guard — 30-дн відтік усіх каналів vs виручка
  const [guard, setGuard] = useState(null);
  const [gMax, setGMax] = useState(''); const [gMsg, setGMsg] = useState(''); const [gErr, setGErr] = useState('');
  const gInit = React.useRef(false);
  // [D-221 B.2a] кампанії роздачі реклами засновникам
  const [campaigns, setCampaigns] = useState([]);
  const [adName, setAdName] = useState(''); const [adFile, setAdFile] = useState(null);
  const [adMsg, setAdMsg] = useState(''); const [adErr, setAdErr] = useState(''); const [adBusy, setAdBusy] = useState(false);
  // [D-221 B.2d] founder-профілі (соц-акаунти + пруфи + доставки)
  const [foundersDetail, setFoundersDetail] = useState([]);
  const [socForm, setSocForm] = useState({});
  // [Q-061] заявки на вивід реф-бонусів
  const [withdrawals, setWithdrawals] = useState([]);
  // [D-221] перестановка панелей (CSS order + localStorage)
  const [panelOrder, setPanelOrder] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem('stats_panel_order')); return Array.isArray(s) ? s : ['founder', 'guard', 'campaigns']; } catch { return ['founder', 'guard', 'campaigns']; }
  });
  const pord = (id) => { const i = panelOrder.indexOf(id); return i < 0 ? 90 : i; };
  const onPanelDrop = (targetId) => {
    const from = window._pDrag;
    if (!from || from === targetId) return;
    const arr = panelOrder.slice(); const fi = arr.indexOf(from); const ti = arr.indexOf(targetId);
    if (fi < 0 || ti < 0) return;
    arr.splice(fi, 1); arr.splice(ti, 0, from);
    setPanelOrder(arr); try { localStorage.setItem('stats_panel_order', JSON.stringify(arr)); } catch (e) {}
  };
  const _ph = (id) => ({ onDragOver: (e) => e.preventDefault(), onDrop: () => onPanelDrop(id), sx: { order: pord(id) } });
  const _handle = (id) => (<Box draggable onDragStart={() => { window._pDrag = id; }} sx={{ cursor: 'grab', color: '#666', fontSize: 11, userSelect: 'none', mt: 3, mb: 0.3 }}>⠿ перетягнути віконце</Box>);

  const load = () => {
    getStats().then(r => { setStats(r.data); setLoading(false); }).catch(() => setLoading(false));
    // 💎 PlayLanguage: pull the per-product revenue breakdown (Media + PL). Best-effort —
    // if it fails the page still renders the Media-only cards.
    fetch('/api/finance/products', { headers: { Authorization: 'Bearer ' + tok } })
      .then(r => (r.ok ? r.json() : null)).then(p => setProducts(p)).catch(() => {});
    // [D-221 C] founder/promo config — display refresh; editable fields init ONCE (no clobber)
    fetch('/api/founders', { headers: { Authorization: 'Bearer ' + tok } })
      .then(r => (r.ok ? r.json() : null)).then(f => {
        if (!f) return;
        setFounders(f);
        if (!fInit.current) {
          fInit.current = true;
          setFN(String(f.limit)); setFDaily(String(f.daily)); setFL1(String(f.referral_l1));
          const pm = {}; (f.packages || []).forEach(p => { pm[p.duration] = String(p.eur); }); setFP(pm);
        }
      }).catch(() => {});
    // [D-221 C.1] Margin Guard стан
    fetch('/api/margin-guard', { headers: { Authorization: 'Bearer ' + tok } })
      .then(r => (r.ok ? r.json() : null)).then(g => {
        if (!g) return;
        setGuard(g);
        if (!gInit.current) { gInit.current = true; setGMax(String(g.max_percent)); }
      }).catch(() => {});
    // [D-221 B.2a] кампанії реклами
    fetch('/api/ad-campaigns', { headers: { Authorization: 'Bearer ' + tok } })
      .then(r => (r.ok ? r.json() : null)).then(c => { if (Array.isArray(c)) setCampaigns(c); }).catch(() => {});
    // [D-221 B.2d] founder-профілі
    fetch('/api/founders/detail', { headers: { Authorization: 'Bearer ' + tok } })
      .then(r => (r.ok ? r.json() : null)).then(d => { if (Array.isArray(d)) setFoundersDetail(d); }).catch(() => {});
    // [Q-061] заявки на вивід
    fetch('/api/withdrawals', { headers: { Authorization: 'Bearer ' + tok } })
      .then(r => (r.ok ? r.json() : null)).then(d => { if (Array.isArray(d)) setWithdrawals(d); }).catch(() => {});
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const resetStat = async (key) => {
    if (!window.confirm('Скинути показник до 0?')) return;
    await fetch('/api/stats/reset?key='+key, { method:'POST', headers:{ Authorization:'Bearer '+tok } });
    load();
  };
  const restoreStat = async (key) => {
    await fetch('/api/stats/restore?key='+key, { method:'POST', headers:{ Authorization:'Bearer '+tok } });
    load();
  };

  const onDragEnd = (list, from, to) => {
    const o = [...list]; const [m] = o.splice(from,1); o.splice(to,0,m);
    setOrder(o); localStorage.setItem(STORAGE_KEY, JSON.stringify(o));
  };

  // [D-221 C.0] зберегти founder/promo конфіг (валідація на бекенді → 400 з причиною; audit-лог)
  const saveFounderConfig = async () => {
    setFMsg(''); setFErr('');
    try {
      const r = await fetch('/api/founders/config', {
        method: 'POST', headers: { Authorization: 'Bearer ' + tok, 'Content-Type': 'application/json' },
        body: JSON.stringify({ limit: fN, referral_l1: fL1, daily: fDaily, prices: fP }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setFErr((j && j.detail) || 'Помилка збереження'); return; }
      setFMsg('✅ Збережено · змін: ' + j.changes); fInit.current = false; load();
      setTimeout(() => setFMsg(''), 3500);
    } catch { setFErr('Помилка мережі'); }
  };

  // [D-221 C.1] зберегти cap% Margin Guard (валідація 0-100 на бекенді + audit)
  const saveGuard = async () => {
    setGMsg(''); setGErr('');
    try {
      const r = await fetch('/api/margin-guard/config', {
        method: 'POST', headers: { Authorization: 'Bearer ' + tok, 'Content-Type': 'application/json' },
        body: JSON.stringify({ max_percent: gMax }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setGErr((j && j.detail) || 'Помилка'); return; }
      setGMsg('✅ Збережено'); gInit.current = false; load();
      setTimeout(() => setGMsg(''), 3000);
    } catch { setGErr('Помилка мережі'); }
  };

  // [D-221 C.1b] ручний перемикач паузи виплат
  const toggleGuardPause = async () => {
    setGMsg(''); setGErr('');
    try {
      const r = await fetch('/api/margin-guard/config', {
        method: 'POST', headers: { Authorization: 'Bearer ' + tok, 'Content-Type': 'application/json' },
        body: JSON.stringify({ paused: !(guard && guard.paused) }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setGErr((j && j.detail) || 'Помилка'); return; }
      setGMsg('✅ ' + (guard && guard.paused ? 'Пауза знята' : 'Виплати поставлено на паузу')); gInit.current = false; load();
      setTimeout(() => setGMsg(''), 3000);
    } catch { setGErr('Помилка мережі'); }
  };

  // [D-221 C.1b+] звільнити заблоковані нарахування бенефіціара (blocked → frozen + кредит)
  const releaseBlocked = async (uid, label) => {
    if (!window.confirm('Звільнити заблоковані нарахування для ' + label + '?\nВони підуть у нормальний 14-дн freeze + кредит балансу.')) return;
    setGMsg(''); setGErr('');
    try {
      const r = await fetch('/api/margin-guard/release?uid=' + uid, { method: 'POST', headers: { Authorization: 'Bearer ' + tok } });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setGErr((j && j.detail) || 'Помилка'); return; }
      setGMsg('✅ Звільнено: ' + j.released + ' нарахувань на €' + j.amount); load();
      setTimeout(() => setGMsg(''), 3500);
    } catch { setGErr('Помилка мережі'); }
  };

  // [D-221 B.2a] завантажити рекламу-кампанію
  const uploadCampaign = async () => {
    if (!adFile) { setAdErr('Обери відео'); return; }
    setAdMsg(''); setAdErr(''); setAdBusy(true);
    try {
      const fd = new FormData(); fd.append('file', adFile); fd.append('name', adName || adFile.name);
      const r = await fetch('/api/ad-campaigns', { method: 'POST', headers: { Authorization: 'Bearer ' + tok }, body: fd });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setAdErr((j && j.detail) || 'Помилка завантаження'); setAdBusy(false); return; }
      setAdMsg('✅ Кампанію #' + j.id + ' створено'); setAdFile(null); setAdName(''); load();
      setTimeout(() => setAdMsg(''), 3500);
    } catch { setAdErr('Помилка мережі'); }
    setAdBusy(false);
  };
  const distributeCampaign = async (cid) => {
    if (!window.confirm('Роздати кампанію #' + cid + ' усім активним засновникам?\nЗавдання: обробка вночі 00-06 (не чіпаючи платних), доставка вранці 08-10.')) return;
    setAdMsg(''); setAdErr('');
    try {
      const r = await fetch('/api/ad-campaigns/' + cid + '/distribute', { method: 'POST', headers: { Authorization: 'Bearer ' + tok } });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { setAdErr((j && j.detail) || 'Помилка'); return; }
      setAdMsg('✅ Роздано: ' + j.founders + ' засновників → ' + j.deliveries + ' завдань у черзі'); load();
      setTimeout(() => setAdMsg(''), 4000);
    } catch { setAdErr('Помилка мережі'); }
  };
  const deleteCampaign = async (cid) => {
    if (!window.confirm('Видалити кампанію #' + cid + ' (і всі доставки)?')) return;
    try { await fetch('/api/ad-campaigns/' + cid, { method: 'DELETE', headers: { Authorization: 'Bearer ' + tok } }); load(); } catch {}
  };

  // [D-221 B.2d] соц-акаунти founder'а (ручне керування OWNER)
  const addSocial = async (uid) => {
    const f = socForm[uid] || {};
    if (!f.network || !f.url) return;
    try {
      await fetch('/api/founders/social?uid=' + uid + '&network=' + encodeURIComponent(f.network) + '&url=' + encodeURIComponent(f.url),
        { method: 'POST', headers: { Authorization: 'Bearer ' + tok } });
      setSocForm({ ...socForm, [uid]: { network: '', url: '' } }); load();
    } catch {}
  };
  const delSocial = async (uid, network) => {
    try { await fetch('/api/founders/social?uid=' + uid + '&network=' + encodeURIComponent(network), { method: 'DELETE', headers: { Authorization: 'Bearer ' + tok } }); load(); } catch {}
  };

  // [Q-061] обробка заявок на вивід
  const wdPaid = async (w) => {
    if (!window.confirm('Підтвердити: ти ВЖЕ переказав ' + w.amount + '€ @' + w.username + ' (' + w.method + ')?\nБаланс юзера буде списано.')) return;
    try {
      const r = await fetch('/api/withdrawals/' + w.id + '/paid', { method: 'POST', headers: { Authorization: 'Bearer ' + tok } });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) { alert((j && j.detail) || 'Помилка'); return; }
      load();
    } catch {}
  };
  const wdReject = async (w) => {
    const note = window.prompt('Причина відхилення (юзеру видно):', '');
    if (note === null) return;
    try { await fetch('/api/withdrawals/' + w.id + '/reject?note=' + encodeURIComponent(note), { method: 'POST', headers: { Authorization: 'Bearer ' + tok } }); load(); } catch {}
  };
  // [D-221 grace-revert] повернути / зняти статус Засновника
  const restoreFounder = async (uid) => {
    if (!window.confirm('Повернути статус Засновника?')) return;
    try { await fetch('/api/founders/' + uid + '/restore', { method: 'POST', headers: { Authorization: 'Bearer ' + tok } }); load(); } catch {}
  };
  const revokeFounder = async (uid) => {
    if (!window.confirm('Зняти статус Засновника?\n(Поточна підписка дограє за старою ціною; наступна оплата — звичайний пакет.)')) return;
    try { await fetch('/api/founders/' + uid + '/revoke', { method: 'POST', headers: { Authorization: 'Bearer ' + tok } }); load(); } catch {}
  };

  if (loading) return <Box sx={{ display:'flex', justifyContent:'center', mt:10 }}><CircularProgress sx={{ color:'var(--ac1, #7c4dff)' }}/></Box>;
  if (!stats) return <Typography sx={{ color:'#f44' }}>Помилка завантаження</Typography>;

  // 💎 PlayLanguage (Phase F fix): revenue split. Media card keeps its reset/restore
  // (those act only on Media payments). PL + grand-total are read-only additions.
  const plProd = products && products.products ? products.products.find(p => p.key === 'playlanguage') : null;
  const plRevenue = plProd ? (plProd.total || 0) : null;
  const grandTotal = products ? (products.grand_total || 0) : null;

  const cards = [
    { title:"Всього юзерів", value:stats.total_users, color:"var(--ac1, #7c4dff)", icon:"👥", onClick:() => navigate('/users') },
    { title:"Активні", value:stats.active_users, color:"#4caf50", icon:"✅", onClick:() => navigate('/users?filter=active') },
    { title:"Тріал", value:stats.trial_users, color:"#ff9800", icon:"⏳", onClick:() => navigate('/users?filter=trial') },
    { title:"Відео сьогодні", value:stats.videos_today+' ('+(stats.versions_today||0)+' верс.)', color:"#2196f3", icon:"🎬",
      onClick:() => navigate('/videos'), onReset:() => resetStat('videos_today'), onRestore:() => restoreStat('videos_today') },
    { title:"Дохід Media", value:(stats.total_revenue||0).toFixed(0)+' EUR', color:"#f44336", icon:"💰",
      onClick:() => navigate('/finance'), onReset:() => resetStat('revenue'), onRestore:() => restoreStat('revenue') },
    // [МЕДІА УНІКАЛІЗАТОР] виплачено реф-бонусів (completed-заявки) + к-сть pending
    { title:"💸 Виплачено", value:(withdrawals.filter(w=>w.status==='completed').reduce((s,w)=>s+(w.amount||0),0)).toFixed(0)+' EUR'
        + (withdrawals.filter(w=>w.status==='pending').length ? ' ('+withdrawals.filter(w=>w.status==='pending').length+' очікує)' : ''),
      color:"#ffb74d", icon:"💸", onClick:() => navigate('/users?filter=withdrawals') },
  ];
  // Append PL revenue cards only once the breakdown has loaded (keeps the layout
  // stable on first paint; the order helper below tolerates the extra indices).
  if (products) {
    cards.push({ title:"💎 PL дохід", value:(plRevenue||0).toFixed(0)+' EUR', color:"#5eead4", icon:"💎",
      onClick:() => navigate('/finance') });
    cards.push({ title:"Разом (M+PL)", value:(grandTotal||0).toFixed(0)+' EUR', color:"#69f0ae", icon:"🧮",
      onClick:() => navigate('/finance') });
  }
  // Robust order: keep saved order for known cards, append any new card indices
  // (e.g. the PL cards) that a stale localStorage order doesn't yet know about.
  const visibleOrder = [
    ...order.filter(i => i < cards.length),
    ...cards.map((_, i) => i).filter(i => !order.includes(i)),
  ];

  return (
    <Box>
      <Typography variant="h5" sx={{ color:'#fff', mb:3, fontWeight:'bold' }}>📊 Статистика</Typography>
      <Grid container spacing={2}>
        {visibleOrder.map((cardIdx, i) => {
          const c = cards[cardIdx];
          if (!c) return null;
          return (
            <Grid item xs={12} sm={6} md={3} key={cardIdx}
              draggable onDragStart={() => window._dragFrom = i}
              onDragOver={e => e.preventDefault()}
              onDrop={() => { if (window._dragFrom !== undefined) onDragEnd(visibleOrder, window._dragFrom, i); }}
              sx={{ cursor:'grab' }}>
              <StatCard {...c}/>
            </Grid>
          );
        })}
      </Grid>

      {/* [МЕДІА УНІКАЛІЗАТОР] D-221 C: founder/promo config (перенесено з Налаштувань, тепер редаговане) */}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {founders && (
        <Box {..._ph('founder')}>
        {_handle('founder')}
        <Paper sx={{ bgcolor: '#1a1a2e', border: '1px solid #FFD700', p: 3, mt: 0, maxWidth: 700 }}>
          <Typography variant="h6" sx={{ color: '#FFD700', mb: 1, fontWeight: 'bold' }}>🏆 Когорта «Засновники» / Промо-оферта</Typography>
          <Typography sx={{ color: '#fff', mb: 0.5 }}>Зайнято: <b>{founders.count}/{founders.limit}</b> · лишилось {founders.remaining} · {founders.open ? '🟢 відкрита' : '🔴 закрита'}</Typography>
          <Typography sx={{ color: '#81c784', mb: 2, fontSize: 14 }}>💰 Дохід від засновників: €{founders.revenue}</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField size="small" type="number" label="Ліміт N" value={fN} onChange={e => setFN(e.target.value)} sx={{ ...tfSx, width: 110 }}/>
            <TextField size="small" type="number" label="Унікаліз./день" value={fDaily} onChange={e => setFDaily(e.target.value)} sx={{ ...tfSx, width: 150 }}/>
            <TextField size="small" type="number" label="Реф L1 % (≤25)" value={fL1} onChange={e => setFL1(e.target.value)} sx={{ ...tfSx, width: 150 }}/>
          </Box>
          <Typography sx={{ color: '#aaa', mb: 1, fontSize: 13 }}>Ціна (€) за період:</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            {[1, 3, 6, 12].map(d => (
              <TextField key={d} size="small" type="number" label={d + ' міс'} value={fP[d] ?? ''} onChange={e => setFP({ ...fP, [d]: e.target.value })} sx={{ ...tfSx, width: 95 }}/>
            ))}
          </Box>
          <Button variant="contained" onClick={saveFounderConfig} sx={{ bgcolor: '#FFD700', color: '#1a1a2e', fontWeight: 'bold' }}>💾 Зберегти конфіг</Button>
          {fMsg && <Alert severity="success" sx={{ mt: 2, bgcolor: 'rgba(76,175,80,0.1)', color: '#4caf50' }}>{fMsg}</Alert>}
          {fErr && <Alert severity="error" sx={{ mt: 2, bgcolor: 'rgba(244,67,54,0.1)', color: '#f44336' }}>{fErr}</Alert>}
          <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 1.5 }}>
            ⚠️ Зміна ціни/ліміту діє на МАЙБУТНІ покупки + подовження існуючих засновників (grandfather по політиці: не чіпай ці поля — стара ціна тримається). Для окремої акції з іншими цінами — створи новий пакет у «Пакети». Усі зміни логуються (audit · валідація L1≤25%).
          </Typography>

          {/* [D-221 B.2d] founder-профілі: соц-акаунти (клікабельні) + пруфи + доставки — показуємо ЗАВЖДИ (порожній стан коли 0) */}
          <Box sx={{ mt: 2, borderTop: '1px solid #2a2a3e', pt: 1.5 }}>
              <Typography sx={{ color: '#FFD700', fontSize: 14, mb: 1, fontWeight: 'bold' }}>👥 Засновники ({foundersDetail.length}) — соцмережі та пруфи:</Typography>
              {foundersDetail.length === 0 && <Typography sx={{ color: '#666', fontSize: 13 }}>— поки немає засновників (тут зʼявляться їхні канали + пруфи + статистика доставок, коли хтось стане Засновником через /pay) —</Typography>}
              {foundersDetail.map(f => (
                <Box key={f.uid} sx={{ mb: 1.5, p: 1, border: '1px solid #2a2a3e', borderRadius: 1 }}>
                  <Typography sx={{ color: '#fff', fontSize: 13 }}>@{f.username} · {f.daily}/день{f.sub_end ? (' · до ' + f.sub_end) : ''} · 📤 надіслано {f.deliveries.sent}·готово {f.deliveries.ready}·черга {f.deliveries.queued}{f.deliveries.failed ? ('·збій ' + f.deliveries.failed) : ''}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 0.3 }}>
                    {f.reverted ? (
                      <>
                        <Typography sx={{ color: '#f44336', fontSize: 12 }}>🔻 статус знято {f.reverted_at}</Typography>
                        <Button size="small" variant="outlined" onClick={() => restoreFounder(f.uid)} sx={{ borderColor: '#4caf50', color: '#4caf50', fontSize: 11, minWidth: 0, py: 0 }}>🔁 Повернути статус</Button>
                      </>
                    ) : (
                      <>
                        {f.ignored_streak > 0
                          ? <Typography sx={{ color: '#ff9800', fontSize: 12 }}>⚠️ відстає {f.ignored_streak} дн{f.warned ? ' (попереджено)' : ''}</Typography>
                          : <Typography sx={{ color: '#81c784', fontSize: 12 }}>✅ комплаєнс ок</Typography>}
                        <Button size="small" onClick={() => revokeFounder(f.uid)} sx={{ color: '#f44', fontSize: 10, minWidth: 0, py: 0 }}>зняти статус</Button>
                      </>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', mt: 0.5 }}>
                    <Typography sx={{ color: '#888', fontSize: 12, mr: 0.5 }}>📱</Typography>
                    {f.socials.map(s => (
                      <span key={s.network} style={{ fontSize: 12, marginRight: 6 }}>
                        <a href={s.url} target="_blank" rel="noreferrer" style={{ color: '#5eead4' }}>{s.network} ↗</a>
                        <span onClick={() => delSocial(f.uid, s.network)} style={{ color: '#f44', cursor: 'pointer', marginLeft: 3 }}>✕</span>
                      </span>
                    ))}
                    {f.socials.length === 0 && <Typography sx={{ color: '#555', fontSize: 12 }}>— нема соцмереж —</Typography>}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
                    <TextField size="small" placeholder="мережа" value={(socForm[f.uid] || {}).network || ''} onChange={e => setSocForm({ ...socForm, [f.uid]: { ...(socForm[f.uid] || {}), network: e.target.value } })} sx={{ ...tfSx, width: 110 }}/>
                    <TextField size="small" placeholder="посилання на канал" value={(socForm[f.uid] || {}).url || ''} onChange={e => setSocForm({ ...socForm, [f.uid]: { ...(socForm[f.uid] || {}), url: e.target.value } })} sx={{ ...tfSx, width: 210 }}/>
                    <Button size="small" variant="outlined" onClick={() => addSocial(f.uid)} sx={{ borderColor: '#5eead4', color: '#5eead4', fontSize: 11, minWidth: 0 }}>+ соцмережа</Button>
                  </Box>
                  {f.proofs.length > 0 && (
                    <Typography sx={{ color: '#888', fontSize: 12, mt: 0.5 }}>📋 Пруфи ({f.proof_count}): {f.proofs.map((p, i) => (<a key={i} href={p.url} target="_blank" rel="noreferrer" style={{ color: '#81c784', marginRight: 6 }}>{p.network || 'link'} ↗</a>))}</Typography>
                  )}
                </Box>
              ))}
            </Box>
        </Paper>
        </Box>
      )}

      {/* [МЕДІА УНІКАЛІЗАТОР] D-221 C.1: Unified Margin Guard — видимість відтоку всіх каналів */}
      {guard && (
        <Box {..._ph('guard')}>
        {_handle('guard')}
        <Paper sx={{ bgcolor: '#1a1a2e', border: '1px solid ' + (guard.status === 'crit' ? '#f44336' : guard.status === 'warn' ? '#ff9800' : '#4caf50'), p: 3, mt: 0, maxWidth: 700 }}>
          <Typography variant="h6" sx={{ color: guard.status === 'crit' ? '#f44336' : guard.status === 'warn' ? '#ff9800' : '#4caf50', mb: 1, fontWeight: 'bold' }}>🛡️ Margin Guard — відтік за 30 днів</Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 1 }}>
            <Typography sx={{ color: '#fff' }}>Виручка: <b>€{guard.revenue_30d}</b></Typography>
            <Typography sx={{ color: '#fff' }}>Відтік: <b>€{guard.outflow_30d}</b></Typography>
            <Typography sx={{ color: guard.over_cap ? '#f44336' : '#81c784' }}>Ratio: <b>{guard.ratio}%</b> / cap {guard.max_percent}%</Typography>
          </Box>
          {guard.over_cap && <Alert severity="error" sx={{ mb: 1, bgcolor: 'rgba(244,67,54,0.1)', color: '#f44336' }}>🔴 Відтік перевищив cap — час перевірити канали!</Alert>}
          {guard.paused && <Alert severity="warning" sx={{ mb: 1, bgcolor: 'rgba(255,152,0,0.1)', color: '#ff9800' }}>⏸️ Виплати на ПАУЗІ — нові реф-нарахування пишуться як «blocked» (не кредитуються). Перевір і зніми паузу.</Alert>}
          {guard.blocked_count > 0 && (
            <Box sx={{ mb: 1, p: 1.5, border: '1px solid #ff9800', borderRadius: 1, bgcolor: 'rgba(255,152,0,0.05)' }}>
              <Typography sx={{ color: '#ff9800', fontSize: 13, mb: 0.5 }}>🚫 Заблоковано: {guard.blocked_count} нарахувань на €{guard.blocked_sum} — рев'ю по бенефіціарах:</Typography>
              {(guard.blocked_list || []).map(b => (
                <Box key={b.uid} sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'space-between', py: 0.3, flexWrap: 'wrap' }}>
                  <Typography sx={{ color: '#ddd', fontSize: 13 }}>@{b.username} — <b>€{b.total}</b> ({b.count} шт · {b.last_at})</Typography>
                  <Button size="small" variant="outlined" onClick={() => releaseBlocked(b.uid, '@' + b.username)} sx={{ borderColor: '#4caf50', color: '#4caf50', fontSize: 11, minWidth: 0, py: 0 }}>▶️ Звільнити</Button>
                </Box>
              ))}
            </Box>
          )}
          <Typography sx={{ color: '#aaa', fontSize: 13, mb: 0.5 }}>Канали відтоку (30 днів):</Typography>
          <Box sx={{ mb: 2 }}>
            {(guard.channels || []).map(c => (
              <Typography key={c.channel} sx={{ color: '#888', fontSize: 13 }}>• {c.channel}: €{c.amount}</Typography>
            ))}
            {(!guard.channels || guard.channels.length === 0) && <Typography sx={{ color: '#555', fontSize: 13 }}>— поки немає відтоку —</Typography>}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField size="small" type="number" label="Cap % (макс відтік)" value={gMax} onChange={e => setGMax(e.target.value)} sx={{ ...tfSx, width: 180 }}/>
            <Button variant="contained" onClick={saveGuard} sx={{ bgcolor: 'var(--ac1, #7c4dff)' }}>💾 Зберегти cap</Button>
            <Button variant="outlined" onClick={toggleGuardPause} sx={{ borderColor: guard.paused ? '#4caf50' : '#ff9800', color: guard.paused ? '#4caf50' : '#ff9800' }}>{guard.paused ? '▶️ Зняти паузу' : '⏸️ Пауза виплат'}</Button>
          </Box>
          {gMsg && <Alert severity="success" sx={{ mt: 2, bgcolor: 'rgba(76,175,80,0.1)', color: '#4caf50' }}>{gMsg}</Alert>}
          {gErr && <Alert severity="error" sx={{ mt: 2, bgcolor: 'rgba(244,67,54,0.1)', color: '#f44336' }}>{gErr}</Alert>}
          <Typography variant="caption" sx={{ color: '#888', display: 'block', mt: 1.5 }}>
            Відтік = реферальна (усі рівні) + майбутні канали (контести/кешбек/топ пишуть у outflow_ledger коли зʼявляться). 🛡️ Авто-пауза: коли ratio перевищує cap — нові реф-нарахування блокуються (статус «blocked», не кредитуються) + видно тут; знімаєш паузу вручну після перевірки.
          </Typography>
        </Paper>
        </Box>
      )}

      {/* [МЕДІА УНІКАЛІЗАТОР] D-221 B.2a: роздача реклами засновникам */}
      <Box {..._ph('campaigns')}>
      {_handle('campaigns')}
      <Paper sx={{ bgcolor: '#1a1a2e', border: '1px solid #2196f3', p: 3, mt: 0, maxWidth: 720 }}>
        <Typography variant="h6" sx={{ color: '#2196f3', mb: 1, fontWeight: 'bold' }}>📤 Реклама засновникам (роздача)</Typography>
        <Typography sx={{ color: '#888', fontSize: 13, mb: 2 }}>Заливаєш відео → «Роздати» → унікалізатор вночі (00-06, не чіпаючи платних) робить унікальну версію кожному → ранком (08-10) приходить у бот. Founder тримає всі свої 9.</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
          <Button variant="outlined" component="label" size="small" sx={{ borderColor: '#555', color: '#aaa' }}>
            📎 Відео<input type="file" hidden accept="video/*,.mp4,.mov,.avi,.mkv,.webm" onChange={e => setAdFile(e.target.files[0])}/>
          </Button>
          {adFile && <Typography variant="caption" sx={{ color: '#4caf50' }}>✅ {adFile.name}</Typography>}
          <TextField size="small" placeholder="Назва (необов.)" value={adName} onChange={e => setAdName(e.target.value)} sx={{ ...tfSx, width: 180 }}/>
          <Button variant="contained" disabled={adBusy || !adFile} onClick={uploadCampaign} sx={{ bgcolor: '#2196f3' }}>{adBusy ? '⏳…' : '⬆️ Завантажити'}</Button>
        </Box>
        {adMsg && <Alert severity="success" sx={{ mb: 1, bgcolor: 'rgba(76,175,80,0.1)', color: '#4caf50' }}>{adMsg}</Alert>}
        {adErr && <Alert severity="error" sx={{ mb: 1, bgcolor: 'rgba(244,67,54,0.1)', color: '#f44336' }}>{adErr}</Alert>}
        {campaigns.map(c => (
          <Box key={c.id} sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'space-between', py: 0.5, borderTop: '1px solid #2a2a3e', flexWrap: 'wrap' }}>
            <Typography sx={{ color: '#ddd', fontSize: 13 }}>#{c.id} {c.name} {c.has_video ? '🎬' : '⚠️'} — черга {c.queued}·обробка {c.processing}·готово {c.ready}·надіслано {c.sent}{c.failed ? ('·збій ' + c.failed) : ''}</Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button size="small" variant="outlined" onClick={() => distributeCampaign(c.id)} sx={{ borderColor: '#2196f3', color: '#2196f3', fontSize: 11, minWidth: 0 }}>📤 Роздати</Button>
              <Button size="small" onClick={() => deleteCampaign(c.id)} sx={{ color: '#f44', fontSize: 11, minWidth: 0 }}>🗑</Button>
            </Box>
          </Box>
        ))}
        {campaigns.length === 0 && <Typography sx={{ color: '#555', fontSize: 13, mt: 1 }}>— кампаній ще немає —</Typography>}
        <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 1.5 }}>⚙️ Обробка/доставка — фоновими воркерами. «Роздати» створює завдання у черзі. 🖱️ Перетягни «⠿ перетягнути віконце» щоб поміняти панелі місцями (порядок зберігається).</Typography>
      </Paper>
      </Box>

      {/* [МЕДІА УНІКАЛІЗАТОР] Q-061: панель заявок на вивід ПЕРЕНЕСЕНО → Користувачі → 💸 Заявки на вивід */}
      </Box>
    </Box>
  );
}
